import { generate } from '../../../core/js/utils/componentUtils';

import { init as initFacet } from './facetComponent';
import { init as initMobileFacetContainer } from './mobileFacetsContainer';

import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';

import template from 'facets/category-facet-manager.j2';

import addDropdownSupport from '../../../core/js/uiSupport/dropDown';
import addAccordionSupport from '../../../core/js/uiSupport/accordion';

import { getFlatNavigationSync } from '@core/js/utils/navigationUtils';
import { shouldShowDisabledNavigation } from '@category/js/utils/utils';

const MOBILE_COMPONENTS = 'small';
const DESKTOP_COMPONENTS = 'large';

function onStateChange(store, watchValue, oldVal, newVal) {
    // temporarily disable responding to device size change
    // (which could be triggered while creating new child components)
    try {
        this.removeWatchers(store);
        this.useCorrectFilterComponents(store, newVal);
    } finally {
        this.removeWatchers = this.attachWatchers(store);
    }
}

function afterConstruction(store) {
    this.useCorrectFilterComponents(store);
}

function generateDictionary(state, dictionary, config) {
    const categoryContext = _.get(state, 'category.context');
    const hideNavAndFacets = _.get(state, 'category.hideNavigationAndFacets', false);
    const showSecondaryNav = _.get(state, 'category.showSecondaryNav', false);
    const hideFacets = _.get(state, 'category.hideFacets', false);
    const isSmallish = _.get(dictionary, 'core.deviceCharacteristics.is_smallish', false);

    const shouldShowDisabledNav = shouldShowDisabledNavigation(
        categoryContext,
        hideNavAndFacets,
        showSecondaryNav,
        !hideFacets,
        isSmallish
    );

    return Object.assign({}, state, {
        core: Object.assign({}, state.core, {
            deviceCharacteristics: _.get(dictionary, 'core.deviceCharacteristics', {})
        }),
        category: Object.assign({}, state.category, {
            showDisabledNav: shouldShowDisabledNav
        })
    });
}

function swapChildren(newChildObjects, store, latestDeviceCharacteristics) {
    const childrenAlreadyExist = _.size(this.childObjects) > 0;

    if (this.childObjects === newChildObjects) {
        return;
    }

    if (this.childObjects) {
        _.forEach(this.childObjects, (child) => {
            this.deactivateChild(child, store);
        });
    }

    this.childObjects = newChildObjects;

    // only render if child components have been created (from a previous device size change)
    if (childrenAlreadyExist) {
        $(this.scopedSelector()).empty();

        const state = store.getState();
        this.render(store, this, Object.assign({}, state, {
            core: Object.assign({}, state.core, {
                deviceCharacteristics: latestDeviceCharacteristics
            })
        }));
    }

    if (this.childObjects) {
        _.forEach(this.childObjects, (child) => {
            this.reactivateChild(child, store);
        });
    }
}

function afterRender(store) {
    addDropdownSupport(this, this.scopedSelector());
    addAccordionSupport(this, this.scopedSelector());
}

function createChildComponentsIfNecessary(config, store, sizeType) {
    switch (sizeType) {
        case MOBILE_COMPONENTS:
            if (!config.smallScreenChildObjects) {
                config.smallScreenChildObjects = createSmallScreenChildObjects(store);
            }
            break;
        case DESKTOP_COMPONENTS:
            if (!config.nonSmallScreenChildObjects) {
                config.nonSmallScreenChildObjects = createNonSmallScreenChildObjects(store);
            }
            break;
    }
}

function useCorrectFilterComponents(store, latestDeviceCharacteristics) {
    let deviceCharacteristics = latestDeviceCharacteristics;

    if (!deviceCharacteristics) {
        deviceCharacteristics = this.getDeviceCharacteristics();
    }

    if (deviceCharacteristics.is_smallish) {
        createChildComponentsIfNecessary(this, store, MOBILE_COMPONENTS);
        this.swapChildren(this.smallScreenChildObjects, store, deviceCharacteristics);
    } else {
        createChildComponentsIfNecessary(this, store, DESKTOP_COMPONENTS);
        this.swapChildren(this.nonSmallScreenChildObjects, store, deviceCharacteristics);
    }
}

function constructComponents(componentInitFuncs, store) {
    let result = {};

    _.forEach(componentInitFuncs, (componentInit) => {
        const componentObject = componentInit(store);

        result = Object.assign({},
            result,
            componentObject
        );
    });

    return result;
}

function createSmallScreenChildObjects(store) {
    const smallScreenFilterComponents = [initMobileFacetContainer];

    return constructComponents(smallScreenFilterComponents, store);
}

function createNonSmallScreenChildObjects(store) {
    const nonSmallScreenChildObjects = [initFacet];

    return constructComponents(nonSmallScreenChildObjects, store);
}

function beforeRender(store) {
    const state = store.getState();
    const showSecondaryNav = _.get(state, 'category.showSecondaryNav', false);
    const hideNavAndFacets = _.get(state, 'category.hideNavigationAndFacets', false);

    if (!hideNavAndFacets && showSecondaryNav) {
        // make sure flat nav is loaded before rendering this component
        // as it's needed by the secondary nav's template
        getFlatNavigationSync(store);
    }
}

/**
 * Bootstrap the component onto the page
 * @param {object} store - The store
 * @param {object} overrides - The overrides object
 * @returns {undefined}
 */
export function init(store, overrides = {}) {
    const config = {
        watch: ['core.deviceCharacteristics'],
        templateLocation: '.dom-category-facet-manager',
        template: template,
        name: 'facetComponentManager',
        onStateChange: onStateChange,
        afterConstruction: afterConstruction,
        afterRender: afterRender,
        smallScreenChildObjects: null,
        nonSmallScreenChildObjects: null,
        useCorrectFilterComponents: useCorrectFilterComponents,
        swapChildren: swapChildren,
        generateDictionary: generateDictionary,
        beforeRender: beforeRender
    };

    return generate(config, store, overrides, getDefaultOptions(store));
}
